import { t } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useFollowUpGuard } from '@/pages/FollowUpReport/guards/useFollowUpGuard'
import { ESearchType } from '@/pages/RunReport/types'
import { useLazyGetSubjectBasicInformationQuery } from '@/store/api/Person/Person.query'
import { useListOfUseCasesQuery } from '@/store/api/RunReport/RunReport.query'
import { selectRunReport } from '@/store/api/RunReport/RunReport.selector'
import {
  setSearchType,
  setUseCases,
} from '@/store/api/RunReport/RunReport.slice'

import { UseCaseFollowUpReportSection } from './components/UseCaseFollowUpReportSection'
import { EFollowUpStep } from './types'

export const NewFollowUpReportUseCase = () => {
  useFollowUpGuard(EFollowUpStep.UseCase)

  const [searchParams] = useSearchParams()
  const reportId = searchParams.get('reportId') ?? ''

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const {
    i18n: { language },
  } = useTranslation()

  const [isLoadingSettingFollowUpReport, setIsLoadingSettingFollowUpReport] =
    useState<boolean>(false)

  // Subject basic information
  const [getSubjectBasicInformation] = useLazyGetSubjectBasicInformationQuery()

  // Use case
  const {
    data: useCasesData,
    isLoading: isLoadingUseCasesData,
    isError: isErrorUseCasesData,
  } = useListOfUseCasesQuery()

  const useCasesDefaultValues = useSelector(selectRunReport).useCases

  const [selectedUseCaseId, setSelectedUseCaseId] = useState<string>(
    useCasesDefaultValues.selectedUseCaseId,
  )
  const [selectedUseCaseName, setSelectedUseCaseName] = useState<string>(
    useCasesDefaultValues.selectedUseCaseName,
  )
  const initialSearchType =
    useSelector(selectRunReport).selectSearchType.searchType
  const [isForCompanySearch, setIsForCompanySearch] = useState<boolean>(
    initialSearchType === ESearchType.Company,
  )
  const [
    selectedUseCaseRequiredComplianceForm,
    setSelectedUseCaseRequiredComplianceForm,
  ] = useState<boolean>(useCasesDefaultValues.consentSwitchValue)

  const handleSetFollowUpReport = async () => {
    setIsLoadingSettingFollowUpReport(true)

    try {
      // Get complete report information
      const { info: reportData } = await getSubjectBasicInformation({
        id: reportId,
      }).unwrap()

      const previousReportHasFCRAUseCase =
        !!reportData.disclosureSigned ||
        !!reportData.summarySigned ||
        !!reportData.dynamicSigned ||
        !!reportData.otherAuthorizationSigned

      if (selectedUseCaseRequiredComplianceForm) {
        setFollowUpUseCase(true)
        navigate(`${AppRoutes.newFollowUpReportConsent}?reportId=${reportId}`, {
          state: {
            consentEmail: reportData.inputs.email,
            previousReportHasFCRAUseCase,
          },
        })
      } else {
        setFollowUpUseCase(false)
        navigate(
          `${AppRoutes.newFollowUpReportLegalDisclaimer}?reportId=${reportId}`,
        )
      }
    } catch (error) {
      catchWithSentry(error)
      toast.error(t('feedback.toast.errorOnSearch'))
      navigate(AppRoutes.reports)
    } finally {
      setIsLoadingSettingFollowUpReport(false)
    }
  }

  const handleSetUseCase = (useCase: string) => {
    const selected = useCasesData?.info.find((item) => item._id === useCase)

    if (selected) {
      const selectedName = selected?.translations.find(
        (t) => t.language === language,
      )
      setSelectedUseCaseName(selectedName?.name || '')
      setSelectedUseCaseId(selected._id)
      setIsForCompanySearch(selected.isForCompanySearch)
      setSelectedUseCaseRequiredComplianceForm(selected.requiredComplianceForm)
    }
  }

  const setFollowUpUseCase = (requireComplianceForm: boolean) => {
    dispatch(
      setUseCases({
        selectedUseCaseId: selectedUseCaseId,
        selectedUseCaseName: selectedUseCaseName,
        consentSwitchValue: requireComplianceForm,
        isSwitchDisabled: isForCompanySearch,
      }),
    )
    dispatch(
      setSearchType(
        isForCompanySearch ? ESearchType.Company : ESearchType.Individual,
      ),
    )
  }

  return (
    <WeScreen
      isLoading={isLoadingUseCasesData || isLoadingSettingFollowUpReport}
      isError={isErrorUseCasesData}
      isDisabledNextButton={(selectedUseCaseId || selectedUseCaseName) === ''}
      onClickNextButton={handleSetFollowUpReport}
    >
      <div className="flex flex-col items-center justify-center h-[85vh]">
        <UseCaseFollowUpReportSection
          data={useCasesData}
          language={language}
          handleUseCaseChange={handleSetUseCase}
          selectedUseCaseName={selectedUseCaseName}
        />
      </div>
    </WeScreen>
  )
}
